/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { FancyTextOptions, FancyTextStyle } from '@appCanvasCommon/interfaces/fancy-text-style.interface';
import { TEXT_GRADIENT, TextStyle } from 'pixi.js-legacy';
/**
 * Contains default tags and default styling.
 * This could be a const instead of a class with static methods I guess. meh ¯\_(ツ)_/¯
 */
export class FancyDefaultStyles {
	/**
	 * The default styling for text.
	 */
	public static DEFAULT_STYLE: FancyTextStyle = new FancyTextStyle({
		align: 'left',
		breakWords: false,
		debugMode: false,
		dropShadow: false,
		dropShadowAngle: Math.PI / 6,
		dropShadowBlur: 0,
		dropShadowColor: '#000000',
		dropShadowDistance: 5,
		fill: 'white',
		fillGradientType: TEXT_GRADIENT.LINEAR_VERTICAL,
		fontFamily: 'Roboto',
		fontSize: 30,
		fontStyle: 'normal',
		fontVariant: 'normal',
		fontWeight: 'normal',
		letterSpacing: 0,
		lineHeight: 0,
		lineJoin: 'miter',
		miterLimit: 10,
		padding: 0,
		resolution: 2,
		stroke: 'black',
		strokeThickness: 0,
		textBaseline: 'alphabetic',
		wordWrap: true,
		wordWrapWidth: 100,
	});
	/**
	 * The default style for all canvas text. This is the POJO ('plain old javascript object') that is intended to be past
	 * into a new FancyTextStyle constructor. The intention for this is to provide a sane "default" object for text that is
	 * safe to modify. If you use the DEFAULT_STYLE and then modify the style, the change will affect every text object in the project.
	 * So using this (or the create() factory constructor), will create a new FancyTextStyle that is unique and safe to modify
	 */
	public static DEFAULT_STYLE_OBJECT: FancyTextOptions = {
		align: 'left',
		breakWords: false,
		debugMode: false,
		dropShadow: false,
		dropShadowAngle: Math.PI / 6,
		dropShadowBlur: 0,
		dropShadowColor: '#000000',
		dropShadowDistance: 5,
		fill: 'white',
		fillGradientType: TEXT_GRADIENT.LINEAR_VERTICAL,
		fontFamily: 'Roboto',
		fontSize: 30,
		fontStyle: 'normal',
		fontVariant: 'normal',
		fontWeight: 'normal',
		letterSpacing: 0,
		lineHeight: 0,
		lineJoin: 'miter',
		miterLimit: 10,
		padding: 0,
		resolution: 2,
		stroke: 'black',
		strokeThickness: 0,
		textBaseline: 'alphabetic',
		wordWrap: true,
		wordWrapWidth: 100,
	};

	/**
	 * Helper function which will create a new instance of a `FancyTextStyle` with sane default values.
	 * Use this when you need a text object that requires style options that are different from the default.
	 */
	public static create(): FancyTextStyle {
		return new FancyTextStyle(FancyDefaultStyles.DEFAULT_STYLE_OBJECT);
	}

	/**
	 * Helper function which will create a new FancyTextStyle with the partial settings
	 * @param options Partial<FancyTextOptions> your custom settings
	 */
	public static createWith(options: Partial<FancyTextOptions>): FancyTextStyle {
		const settings: FancyTextOptions = { ...options, ...FancyDefaultStyles.DEFAULT_STYLE_OBJECT };
		return new FancyTextStyle(settings);
	}
}
